import type { AdminApiFunnel } from "@repo/app-types/api/funnel";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { useFunnelAggregatesQuery } from "../../../api/entities/funnel.js";
import { FunnelFlowChart } from "./funnelFlowChart.js";
import { FunnelSettingsModal } from "./funnelSettingsModal.js";
import { FunnelStageMoments } from "./funnelStageMoments.js";

export interface FunnelReadyViewProps {
  funnel: AdminApiFunnel;
  /** The agent whose numbers are shown. */
  teammateAgentId: TeammateId;
  /** Whether the selling strategy can be edited. */
  isWritable: boolean;
}

export const FunnelReadyView: FC<FunnelReadyViewProps> = (props) => {
  const { funnel, teammateAgentId, isWritable } = props;
  const stages = funnel.stages ?? [];
  const moments = funnel.moments ?? [];

  // Null is before the first stage.
  const [selectedStageKey, setSelectedStageKey] = useState<string | null>(
    stages[0]?.key ?? null,
  );

  const {
    data: aggregates,
    isLoading,
    isError,
  } = useFunnelAggregatesQuery(
    {
      funnelId: funnel.id,
      voiceAgentTeammateId: teammateAgentId,
    },
    // Labels land in the background; keep the numbers fresh while open.
    { pollingInterval: 60_000 },
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      {isWritable && (
        <div className="flex justify-end">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => setIsSettingsOpen(true)}
          >
            Edit selling strategy
          </Button>
        </div>
      )}
      {isLoading ? (
        <DefaultSkeleton />
      ) : isError || !aggregates ? (
        <p className="text-sm text-red-700">
          We couldn&apos;t load this funnel&apos;s numbers. Refresh to try
          again.
        </p>
      ) : (
        <section className="space-y-6" aria-label="Interaction flow">
          <div className="space-y-1">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Interaction flow
            </Heading>
            <p className="text-sm text-muted-foreground">
              Follow interactions through each stage and see where callers
              leave.
            </p>
          </div>
          <FunnelFlowChart
            stages={stages}
            stageCounts={aggregates.stages.agent}
            selectedStageKey={selectedStageKey}
            onSelectedStageKeyChange={setSelectedStageKey}
          />
          <FunnelStageMoments
            funnelId={funnel.id}
            teammateAgentId={teammateAgentId}
            stages={stages}
            moments={moments}
            aggregates={aggregates}
            stageKey={selectedStageKey}
          />
        </section>
      )}
      {isWritable && (
        <FunnelSettingsModal
          funnel={funnel}
          isOpen={isSettingsOpen}
          onOpenChange={setIsSettingsOpen}
        />
      )}
    </div>
  );
};
